import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, CircularProgress, Alert, Chip,
  List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider
} from '@mui/material';
import {
  People as PeopleIcon, QuestionAnswer, TrendingUp, Warning,
  ArrowUpward, ArrowDownward, EmojiEvents
} from '@mui/icons-material';
import api from '../../services/api';

function StatCard({ icon, label, value, subtext, trend, color = 'primary.main' }) {
  return (
    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h5" fontWeight="bold">{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {subtext && <Typography variant="caption" color="text.secondary">{subtext}</Typography>}
      </Box>
      {trend !== undefined && trend !== 0 && (
        <Chip
          size="small"
          icon={trend > 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
          label={`${Math.abs(trend)}%`}
          color={trend > 0 ? 'success' : 'error'}
          variant="outlined"
        />
      )}
    </Paper>
  );
}

export default function OverviewTab({ schoolId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await api.get(`/api/schools/${schoolId}/overview`);
        setData(res.data.data || res.data);
      } catch (err) {
        setError('Failed to load overview data');
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [schoolId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const counts = data.counts || {};
  const thisWeek = data.thisWeek || {};
  const trends = data.trends || {};
  const heatmap = Array.isArray(data.activityHeatmap) ? data.activityHeatmap : [];
  const topPerformers = Array.isArray(data.topPerformers) ? data.topPerformers : [];
  const gradeDist = data.gradeDistribution || {};

  // needsAttention is an OBJECT: { inactive: [], accuracyDropped: [], inactiveCount, accuracyDroppedCount }
  const attentionObj = data.needsAttention || {};
  const inactiveList = Array.isArray(attentionObj.inactive) ? attentionObj.inactive : [];
  const droppedList = Array.isArray(attentionObj.accuracyDropped) ? attentionObj.accuracyDropped : [];
  const attentionList = [
    ...inactiveList.map(l => ({ ...l, reason: 'Inactive this week' })),
    ...droppedList.map(l => ({ ...l, reason: `Accuracy dropped ${Math.round(l.dropPercentage || 0)}%` }))
  ];
  const attentionCount = (attentionObj.inactiveCount || 0) + (attentionObj.accuracyDroppedCount || 0);

  // Collapse heatmap (day x hour) into daily totals for bar chart
  const dailyTotals = {};
  heatmap.forEach(h => { dailyTotals[h.day] = (dailyTotals[h.day] || 0) + h.count; });
  const dailyBars = Object.entries(dailyTotals)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([day, count]) => ({ day: Number(day), count }));
  const dayNames = ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard icon={<PeopleIcon />} label="Total Learners" value={counts.totalLearners || 0}
            subtext={`${thisWeek.activeLearners || 0} active this week`} trend={trends.activeLearners} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard icon={<QuestionAnswer />} label="Questions This Week"
            value={(thisWeek.questionsAnswered || 0).toLocaleString()} color="success.main" trend={trends.questionsAnswered} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard icon={<TrendingUp />} label="Average Accuracy"
            value={`${thisWeek.accuracy || 0}%`} color="info.main" trend={trends.accuracy} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard icon={<Warning />} label="Needs Attention" value={attentionCount} color="warning.main" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {dailyBars.length > 0 && (
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Weekly Activity</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 120 }}>
                {dailyBars.map((bar, i) => {
                  const max = Math.max(...dailyBars.map(b => b.count), 1);
                  const height = (bar.count / max) * 100;
                  return (
                    <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                      <Box sx={{ height: `${Math.max(height, 4)}%`, bgcolor: 'primary.main', borderRadius: '4px 4px 0 0', minHeight: 4, '&:hover': { bgcolor: 'primary.dark' } }}
                        title={`${dayNames[bar.day]}: ${bar.count} questions`} />
                      <Typography variant="caption" color="text.secondary">{dayNames[bar.day]}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={dailyBars.length > 0 ? 4 : 6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Learners Needing Attention</Typography>
            {attentionList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>All learners are on track</Typography>
            ) : (
              <List dense>
                {attentionList.slice(0, 5).map((learner, i) => (
                  <React.Fragment key={learner._id || learner.userId || i}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>{(learner.username || '?')[0].toUpperCase()}</Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={learner.name || learner.username} secondary={learner.reason}
                        primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                      {learner.thisWeekAccuracy !== undefined && (
                        <Chip label={`${Math.round(learner.thisWeekAccuracy)}%`} size="small" color="error" variant="outlined" />
                      )}
                    </ListItem>
                    {i < attentionList.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {topPerformers.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Top Performers This Week</Typography>
              <List dense>
                {topPerformers.slice(0, 5).map((p, i) => (
                  <React.Fragment key={p._id || i}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: i === 0 ? 'warning.main' : 'primary.main' }}>
                          {i === 0 ? <EmojiEvents fontSize="small" /> : (p.username || '?')[0].toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={p.name || p.username} secondary={`${p.answered} questions · Grade ${p.grade}`}
                        primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                      <Chip label={`${Math.round(p.accuracy)}%`} size="small" color={p.accuracy >= 75 ? 'success' : 'info'} variant="outlined" />
                    </ListItem>
                    {i < topPerformers.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {Object.keys(gradeDist).length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Grade Distribution</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(gradeDist).sort((a, b) => Number(a[0]) - Number(b[0])).map(([grade, count]) => (
                  <Chip key={grade} label={`Grade ${grade}: ${count} learners`} color="primary" variant="outlined" />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
